import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import BuyCoinsBarsServices from '../../services/buy-coins-bars.service';
import { userStore } from 'store/UserStore';
import { toast } from 'react-toastify';
import { usePhysicalDeliveryStore } from 'store/DeliveryStore';
import Alert from 'shared/common/alert';
import "../../styles/AddressModal.css";
import CustomerOnboardingHttp from '../../../customer-onboarding/services/customer.service';

const AddressModal = ({ show, addresses, onClose, onAddressAdded }) => {
  const [isAddingNew, setIsAddingNew] = useState(addresses?.length === 0);
  const [editingAddressId, setEditingAddressId] = useState<string | null>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [deleteAddressId, setDeleteAddressId] = useState<string | null>(null);

  useEffect(() => {
    if (addresses?.length === 0) {
      setIsAddingNew(true);
    } else {
      setIsAddingNew(false);
    }
  }, [addresses]);

  const [customerId] = userStore((state: any) => [state.customerId]);
  const selectedAddress = usePhysicalDeliveryStore(
    (state) => state.selectedAddress
  );
  const selectedProduct = usePhysicalDeliveryStore(
    (state) => state.selectedProduct
  );
  const metal = selectedProduct?.metalCode === 'Ag' ? 'Ag' : 'Au';

  const validationSchema = Yup.object({
    firstName: Yup.string().required('First Name is required'),
    lastName: Yup.string().required('Last Name is required'),
    contactNumber: Yup.string()
      .matches(/^\d{10}$/, 'Mobile number must be 10 digits')
      .required('Mobile number is required'),
    contactNumberPrefix: Yup.string()
      .matches(/^\+\d{1,4}$/, 'Country code must start with "+" and include 1 to 4 digits')
      .required('Country code is required'),
    pinCode: Yup.string()
      .matches(/^\d{6}$/, 'Pincode must be 6 digits')
      .required('Pincode is required'),
    street1: Yup.string().required('Address Line 1 is required'),
    addressline2: Yup.string(),
    landmark: Yup.string(),
    city: Yup.string().required('City is required'),
    state: Yup.string().required('State is required'),
    country: Yup.string().required('Country is required'),
    addressType: Yup.string().required('Address type is required'),
    defaultAddress: Yup.boolean(),
  });

  // console.log(selectedAddress,"@adddressData");

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      contactNumber: '',
      contactNumberPrefix: '+91',
      pinCode: '',
      street1: '',
      street2: '',
      landmark: '',
      area: '',
      city: '',
      state: '',
      country: 'India',
      addressType: 'HOME',
      defaultAddress: false,
    },
    validationSchema,

    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        //  Check pincode serviceability on submit only
        const isServiceable = await BuyCoinsBarsServices.checkIfPincodeIsServiceable(
          values.pinCode,
          metal
        );

        if (!isServiceable?.data?.data?.serviceable) {
          toast.error("Delivery is not available at this pincode");
          setSubmitting(false);
          return;
        }

        let response;
        if (editingAddressId) {
          response = await BuyCoinsBarsServices.updateDeliveryAddress(
            { ...values, customerId },
            editingAddressId
          );
        } else {
          response = await BuyCoinsBarsServices.addDeliveryAddress(values, customerId);
        }

        if (response.status === 200) {
          toast.success(editingAddressId ? 'Address updated successfully!' : 'Address added successfully!');
          usePhysicalDeliveryStore.getState().setSelectedAddress(response.data.data);
          resetForm();
          setIsAddingNew(false);
          setEditingAddressId(null);
          if (onAddressAdded) await onAddressAdded();
        } else {
          toast.error('Failed to save address. Please try again.');
        }
      } catch (error) {
        console.error('Error saving address:', error);
        toast.error('Something went wrong.');
      } finally {
        setSubmitting(false);
      }
    }

  });

  const handleDeleteAddress = async (addressId: string) => {
    try {
      const res = await BuyCoinsBarsServices.deleteDeliveryAddress(addressId);

      if (res?.status === 200) {
        console.log(res?.data?.data?.message);
        toast.success('Address deleted successfully');
        if (onAddressAdded) {
          await onAddressAdded();
        }
        // Optionally refetch address list or update UI here
      } else {
        toast.error('Failed to delete address');
      }
    } catch (error: any) {
      toast.error(error?.message || 'Something went wrong');
    }
  };

  const handleEditAddress = (address: any) => {
    setIsAddingNew(true);
    setEditingAddressId(address.addressId);

    formik.setValues({
      firstName: address.firstName || '',
      lastName: address.lastName || '',
      contactNumberPrefix: address.contactNumberPrefix || '+91',
      contactNumber: address.contactNumber || '',
      pinCode: address.pinCode || '',
      street1: address.street1 || '',
      street2: address.street2 || '',
      landmark: address.landmark || '',
      area: address.area || '',
      city: address.city || '',
      state: address.state || 'Maharashtra',
      country: address.country || 'India',
      addressType: address.addressType || 'HOME',
      defaultAddress: address.defaultAddress || false,
    });
  };


  const handlePincodeChange = async (pincode: string) => {
    formik.setFieldValue('pinCode', pincode); // update Formik

    if (pincode.length === 6) {
      try {
        // Disable the state field while loading
        //  Fetch state from API
        const response: any = await CustomerOnboardingHttp.getStatebyPincode(pincode);

        if (response.status === 200 && response.data) {
          const stateName = response.data?.data?.stateName || '';
          formik.setFieldValue('state', stateName); // set state from API

          // Check serviceability
          const serviceRes = await BuyCoinsBarsServices.checkIfPincodeIsServiceable(pincode, metal);

          if (!serviceRes?.data?.data?.serviceable) {
            toast.error("Delivery is not available at this pincode");
            formik.setFieldValue('state', ''); // clear state if not serviceable
          }
        }
      } catch (error) {
        toast.error('Invalid Pincode or failed to fetch state');
        formik.setFieldValue('state', ''); // clear state on error
      }
    } else {
      formik.setFieldValue('state', ''); // clear state if pincode < 6 digits
    }
  };




  const onSuccess = () => {
    if (deleteAddressId) {
      handleDeleteAddress(deleteAddressId);
    }
    setOpen(false);
    setDeleteAddressId(null);
  };

  const onCancel = () => {
    setOpen(false);
    setDeleteAddressId(null);
  };

  const handleAddressChange = (address: any) => {
    usePhysicalDeliveryStore.getState().setSelectedAddress(address);
    onClose();
  };

  useEffect(() => {
    if (isAddingNew && !editingAddressId) {
      formik.resetForm();
    }
  }, [isAddingNew, editingAddressId]);

  return (
    <Modal
      className='custom-address-modal'
      show={show}
      onHide={onClose}
      centered
      size='lg'
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {isAddingNew ? 'Add New Address' : 'Select Delivery Address'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {isAddingNew ? (
          <Form className='custom-address-form' onSubmit={formik.handleSubmit}>
            <Row className='mb-3'>
              <Col xs={6}>
                <Form.Label>First Name</Form.Label>
                <Form.Control
                  type='text'
                  name='firstName'
                  {...formik.getFieldProps('firstName')}
                />
                {formik.touched.firstName && formik.errors.firstName && (
                  <div className='text-danger  small'>
                    {formik.errors.firstName}
                  </div>
                )}
              </Col>
              <Col xs={6}>
                <Form.Label>Last Name</Form.Label>
                <Form.Control
                  type='text'
                  name='lastName'
                  {...formik.getFieldProps('lastName')}
                />
                {formik.touched.lastName && formik.errors.lastName && (
                  <div className='text-danger  small'>
                    {formik.errors.lastName}
                  </div>
                )}
              </Col>
            </Row>

            <Row className='mb-3'>
              <Col xs={6}>
                <Form.Label>Mobile Number</Form.Label>
                <div className='d-flex gap-2'>
                  <Form.Control
                    type='text'
                    name='contactNumberPrefix'
                    placeholder='+91 (IND)'
                    disabled
                    style={{ width: '43px' }}
                    {...formik.getFieldProps('contactNumberPrefix')}
                  />
                  <Form.Control
                    type='text'
                    name='contactNumber'
                    placeholder='Enter mobile number'
                    {...formik.getFieldProps('contactNumber')}
                  />
                </div>
                {formik.touched.contactNumber &&
                  formik.errors.contactNumber && (
                    <div className='text-danger small'>
                      {formik.errors.contactNumber}
                    </div>
                  )}
              </Col>

              <Col xs={6}>
                <Form.Label>Pincode</Form.Label>
                <Form.Control
                  type='text'
                  name='pinCode'
                  maxLength={6}
                  value={formik.values.pinCode}
                  onChange={(e) => handlePincodeChange(e.target.value)}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.pinCode && formik.errors.pinCode && (
                  <div className='text-danger small'>{formik.errors.pinCode}</div>
                )}
              </Col>
            </Row>

            <Row className='mb-3'>
              <Col xs={12}>
                <Form.Label>Address Line 1</Form.Label>
                <Form.Control
                  type='text'
                  name='street1'
                  {...formik.getFieldProps('street1')}
                />
                {formik.touched.street1 && formik.errors.street1 && (
                  <div className='text-danger  small'>
                    {formik.errors.street1}
                  </div>
                )}
              </Col>
            </Row>

            <Row className='mb-3'>
              <Col xs={12}>
                <Form.Label>Address Line 2</Form.Label>
                <Form.Control
                  type='text'
                  name='street2'
                  {...formik.getFieldProps('street2')}
                />
                {formik.touched.street2 && formik.errors.street2 && (
                  <div className='text-danger  small'>
                    {formik.errors.street2}
                  </div>
                )}
              </Col>
            </Row>

            <Row className='mb-3'>
              <Col xs={12}>
                <Form.Label>Landmark</Form.Label>
                <Form.Control
                  type='text'
                  name='landmark'
                  {...formik.getFieldProps('landmark')}
                />
                {formik.touched.landmark && formik.errors.landmark && (
                  <div className='text-danger  small'>
                    {formik.errors.landmark}
                  </div>
                )}
              </Col>
            </Row>

            <Row className='mb-3'>
              <Col xs={6}>
                <Form.Label>City</Form.Label>
                <Form.Control
                  type='text'
                  name='city'
                  {...formik.getFieldProps('city')}
                />
                {formik.touched.city && formik.errors.city && (
                  <div className='text-danger  small'>{formik.errors.city}</div>
                )}
              </Col>
              <Col xs={6}>
                <Form.Label>State</Form.Label>
                <Form.Control
                  type="text"
                  name="state"
                  value={formik.values.state}
                  readOnly
                />
              </Col>
            </Row>

            <Row className='mb-3'>
              <Col xs={12}>
                <Form.Label>Country</Form.Label>
                <Form.Select
                  name='country'
                  {...formik.getFieldProps('country')}
                >
                  <option value='India'>India</option>
                </Form.Select>
              </Col>
            </Row>
            <Row className='mb-2'>
              <Col xs={12}>
                <Form.Label>Address Type</Form.Label>
                <div className='custom-address-type-options'>
                  <Form.Check
                    type='radio'
                    label='Home'
                    name='addressType'
                    value='HOME'
                    checked={formik.values.addressType === 'HOME'}
                    onChange={formik.handleChange}
                  />
                  <Form.Check
                    type='radio'
                    label='Office'
                    name='addressType'
                    value='WORK'
                    checked={formik.values.addressType === 'WORK'}
                    onChange={formik.handleChange}
                  />
                </div>
              </Col>
            </Row>

            <Row className='mb-3'>
              <Col xs={12}>
                <div className='custom-checkbox'>
                  <Form.Check
                    type='checkbox'
                    id='defaultAddress'
                    name='defaultAddress'
                    checked={formik.values.defaultAddress}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  <label htmlFor='defaultAddress'>
                    Make this as default address
                  </label>
                </div>
              </Col>
            </Row>

            <div className='custom-modal-buttons'>
              <Button
                variant='secondary'
                className='custom-cancel-btn'
                onClick={() => setIsAddingNew(false)}
              >
                Cancel
              </Button>
              <Button className='custom-save-btn' type='submit'>
                Save Address
              </Button>
            </div>
          </Form>
        ) : (
          <>
            <div className='custom-address-list'>
              {addresses.map((addr) => (
                <div key={addr.addressId} className='custom-address-entry'>
                  <div className='custom-address-info'>
                    <label className='custom-address-radio'>
                      <input
                        type='radio'
                        value={addr.addressId}
                        checked={selectedAddress?.addressId === addr.addressId}
                        onChange={() => handleAddressChange(addr)}
                      />
                      <span className='custom-address-name'>
                        {addr.firstName} {addr.lastName}
                      </span>
                      <span
                        className={`custom-address-badge ${addr.addressType.toLowerCase()}`}
                      >
                        {addr.addressType}
                      </span>
                    </label>
                    <p>
                      {addr.street1 && `${addr.street1}, `}
                      {addr.street2 && `${addr.street2}, `}
                      {addr.city && `${addr.city}, `}
                      {addr.state && `${addr.state}, `}
                      {addr.country && `${addr.country}`}
                      {addr.pinCode && ` - ${addr.pinCode}`}
                    </p>

                    <p>
                      <strong>Contact:</strong> {addr.contactNumber}
                    </p>
                  </div>
                  <div className='custom-address-actions'>
                    <button
                      className='custom-edit-btn'
                      onClick={() => handleEditAddress(addr)}
                    >
                      Edit
                    </button>
                    <span className='custom-address-action-span'>|</span>
                    {/* <button className='custom-delete-btn' onClick={() => handleDeleteAddress(addr.addressId)} */}
                    <button
                      className='custom-delete-btn'
                      onClick={() => {
                        setDeleteAddressId(addr.addressId); // Set the current addressId
                        setOpen(true); // Open the confirmation dialog
                      }}
                    >
                      Delete
                    </button>
                  </div>
                  <Alert
                    open={open}
                    message='Are you sure you want to delete this delivery address?'
                    onSuccess={onSuccess}
                    onCancel={onCancel}
                  />
                </div>
              ))}
            </div>

            <div className='custom-add-new-address-btn'>
              <Button
                className='custom-add-address-btn'
                onClick={() => {
                  setIsAddingNew(true);
                  setEditingAddressId(null);
                }}
              >
                + Add New Address
              </Button>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default AddressModal;
